import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  EMPTY_HOME_CONFIG,
  defaultBlockConfig,
  normalizeBlockConfig,
  normalizeServicesColumns,
  normalizeProductsColumns,
  type HomeBlockConfig,
  type MerchantHomeConfig,
} from '@/lib/homeBlocks';

const TABLE = 'merchant_home_config';

interface UseHomeConfigResult {
  config: MerchantHomeConfig;
  loading: boolean;
  error: string | null;
  saveBlocks: (blocks: HomeBlockConfig[]) => Promise<boolean>;
  setDefaultBookingService: (serviceId: number | null) => Promise<boolean>;
  setServicesColumns: (columns: number) => Promise<boolean>;
  setProductsColumns: (columns: number) => Promise<boolean>;
  saveAbout: (title: string, content: string) => Promise<boolean>;
  saveCta: (data: { title: string; text: string; buttonLabel: string; url: string }) => Promise<boolean>;
  setPromoProduct: (productId: number | null) => Promise<boolean>;
  saveOpeningHours: (hours: unknown | null) => Promise<boolean>;
  refresh: () => Promise<void>;
}

/**
 * Lit et enregistre la configuration de la page d'accueil d'un marchand
 * (blocs affichés + ordre, calendrier de réservation par défaut).
 * `merchantId` = idcommerce (isolation multi-tenant).
 */
export function useHomeConfig(merchantId: number | null): UseHomeConfigResult {
  const [config, setConfig] = useState<MerchantHomeConfig>(EMPTY_HOME_CONFIG);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!merchantId) {
      setConfig(EMPTY_HOME_CONFIG);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from(TABLE)
        .select('blocks, default_booking_service_id, services_columns, products_columns, about_title, about_content, cta_title, cta_text, cta_button_label, cta_url, promo_product_id, opening_hours')
        .eq('idcommerce', merchantId)
        .maybeSingle();
      if (fetchError) throw fetchError;
      const row = data as {
        blocks?: unknown;
        default_booking_service_id?: number | null;
        services_columns?: number | null;
        products_columns?: number | null;
        about_title?: string | null;
        about_content?: string | null;
        cta_title?: string | null;
        cta_text?: string | null;
        cta_button_label?: string | null;
        cta_url?: string | null;
        promo_product_id?: number | null;
        opening_hours?: unknown;
      } | null;
      setConfig({
        blocks: normalizeBlockConfig(row?.blocks ?? defaultBlockConfig()),
        defaultBookingServiceId:
          row?.default_booking_service_id != null ? Number(row.default_booking_service_id) : null,
        servicesColumns: normalizeServicesColumns(row?.services_columns),
        productsColumns: normalizeProductsColumns(row?.products_columns),
        aboutTitle: typeof row?.about_title === 'string' ? row.about_title : '',
        aboutContent: typeof row?.about_content === 'string' ? row.about_content : '',
        ctaTitle: typeof row?.cta_title === 'string' ? row.cta_title : '',
        ctaText: typeof row?.cta_text === 'string' ? row.cta_text : '',
        ctaButtonLabel: typeof row?.cta_button_label === 'string' ? row.cta_button_label : '',
        ctaUrl: typeof row?.cta_url === 'string' ? row.cta_url : '',
        promoProductId: row?.promo_product_id != null ? Number(row.promo_product_id) : null,
        openingHours: row?.opening_hours ?? null,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de charger la configuration.');
      setConfig(EMPTY_HOME_CONFIG);
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const upsert = useCallback(
    async (payload: Record<string, unknown>): Promise<boolean> => {
      if (!merchantId) return false;
      try {
        const { error: upsertError } = await supabase
          .from(TABLE)
          .upsert(
            { idcommerce: merchantId, updated_at: new Date().toISOString(), ...payload },
            { onConflict: 'idcommerce' },
          );
        if (upsertError) throw upsertError;
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur lors de l’enregistrement.');
        return false;
      }
    },
    [merchantId],
  );

  const saveBlocks = useCallback(
    async (blocks: HomeBlockConfig[]): Promise<boolean> => {
      const ok = await upsert({ blocks });
      if (ok) setConfig((prev) => ({ ...prev, blocks }));
      return ok;
    },
    [upsert],
  );

  const setDefaultBookingService = useCallback(
    async (serviceId: number | null): Promise<boolean> => {
      const ok = await upsert({ default_booking_service_id: serviceId });
      if (ok) setConfig((prev) => ({ ...prev, defaultBookingServiceId: serviceId }));
      return ok;
    },
    [upsert],
  );

  const setServicesColumns = useCallback(
    async (columns: number): Promise<boolean> => {
      const value = normalizeServicesColumns(columns);
      const ok = await upsert({ services_columns: value });
      if (ok) setConfig((prev) => ({ ...prev, servicesColumns: value }));
      return ok;
    },
    [upsert],
  );

  const setProductsColumns = useCallback(
    async (columns: number): Promise<boolean> => {
      const value = normalizeProductsColumns(columns);
      const ok = await upsert({ products_columns: value });
      if (ok) setConfig((prev) => ({ ...prev, productsColumns: value }));
      return ok;
    },
    [upsert],
  );

  const saveAbout = useCallback(
    async (title: string, content: string): Promise<boolean> => {
      const ok = await upsert({ about_title: title, about_content: content });
      if (ok) setConfig((prev) => ({ ...prev, aboutTitle: title, aboutContent: content }));
      return ok;
    },
    [upsert],
  );

  const saveCta = useCallback(
    async (data: { title: string; text: string; buttonLabel: string; url: string }): Promise<boolean> => {
      const ok = await upsert({
        cta_title: data.title,
        cta_text: data.text,
        cta_button_label: data.buttonLabel,
        cta_url: data.url,
      });
      if (ok) {
        setConfig((prev) => ({
          ...prev,
          ctaTitle: data.title,
          ctaText: data.text,
          ctaButtonLabel: data.buttonLabel,
          ctaUrl: data.url,
        }));
      }
      return ok;
    },
    [upsert],
  );

  const setPromoProduct = useCallback(
    async (productId: number | null): Promise<boolean> => {
      const ok = await upsert({ promo_product_id: productId });
      if (ok) setConfig((prev) => ({ ...prev, promoProductId: productId }));
      return ok;
    },
    [upsert],
  );

  const saveOpeningHours = useCallback(
    async (hours: unknown | null): Promise<boolean> => {
      const ok = await upsert({ opening_hours: hours });
      if (ok) setConfig((prev) => ({ ...prev, openingHours: hours }));
      return ok;
    },
    [upsert],
  );

  return {
    config,
    loading,
    error,
    saveBlocks,
    setDefaultBookingService,
    setServicesColumns,
    setProductsColumns,
    saveAbout,
    saveCta,
    setPromoProduct,
    saveOpeningHours,
    refresh,
  };
}